import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { verifyPermission } from "helpers/permission";
import { verifyTokenExpire } from "middleware/verifyToken";
import { formatSelectOption } from "helpers/function";
import { ToastBottomEnd } from "views/components/Toast";
import {
  toastAddErrorMessageConfig,
  toastAddSuccessMessageConfig,
} from "config/toastConfig";
import appConfig from "config/appConfig";

const endPointConfig = {
  PRO: {
    plan: "PRO",
    list: `${appConfig.apiDomaine}/my/staff-claim-object`,
  },
  MACRO: {
    holding: {
      list: `${appConfig.apiDomaine}/any/staff-claim-object`,
    },
    filial: {
      list: `${appConfig.apiDomaine}/my/staff-claim-object`,
    },
  },
  HUB: {
    plan: "HUB",
    list: `${appConfig.apiDomaine}/without-institution/processing-circuits`,
  },
};

const defaultData = {
  institution_id: [],
};

export const useSuperiorRulesData = (plan, userPermissions) => {
  const [loading, setLoading] = useState(true);
  const [staffs, setStaffs] = useState([]);
  const [claimObject, setClaimObject] = useState([]);
  const [data, setData] = useState(undefined);
  const [dataUpdate, setDataUpdate] = useState(undefined);
  const [institutionId, setInstitutionId] = useState(undefined);
  const [error] = useState(defaultData);
  const [startRequest, setStartRequest] = useState(false);
  const [institutionData, setInstitutionData] = useState(undefined);
  const [institution, setInstitution] = useState([]);

  const getEndpoint = useCallback(() => {
    let endPoint = "";
    if (plan === "MACRO") {
      if (
        verifyPermission(
          userPermissions,
          "update-processing-circuit-any-institution"
        )
      ) {
        endPoint = endPointConfig[plan].holding;
      } else if (
        verifyPermission(
          userPermissions,
          "update-processing-circuit-my-institution"
        )
      ) {
        endPoint = endPointConfig[plan].filial;
      }
    } else {
      endPoint = endPointConfig[plan];
    }
    return endPoint;
  }, [plan, userPermissions]);

  useEffect(() => {
    const fetchData = async () => {
      if (!verifyTokenExpire()) return;

      const endPoint = getEndpoint();

      try {
        setLoading(true);

        if (
          verifyPermission(
            userPermissions,
            "update-processing-circuit-any-institution"
          )
        ) {
          const institutionResponse = await axios.get(endPoint.list);
          const options = institutionResponse.data.institutions.length
            ? institutionResponse.data.institutions.map((institution) => ({
                value: institution.id,
                label: institution.name,
              }))
            : [];
          setInstitutionData([options]);
        }

        const response = await axios.get(endPoint.list);

        let newObjectData = [];
        response.data.claimCategories.forEach((claimCategory) =>
          claimCategory.claim_objects.forEach(
            (claimObject) =>
              (newObjectData[claimObject.id] = claimObject.staffs.map(
                (staff) => ({
                  value: staff.id,
                  label: response.data.staffs.map(
                    (staffTemp) => staffTemp.id === staff.id ? `${staffTemp.identite.firstname} ${staffTemp.identite.lastname}` : ''
                  ),
                })
              ))
          )
        );

        setData(newObjectData);
        //setDataUpdate(newObjectData);
        setClaimObject(response.data.claimCategories);
        setStaffs(response.data.staffs);
        setInstitutionId(response.data.institution_id);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [getEndpoint, userPermissions]);

  const onChangeProcessing = useCallback(
    (selectedOptions, objectId) => {

      setData((prevData) => {
        const newData = { ...prevData };
        let all = false;

        const newStaffs = selectedOptions
          ? selectedOptions
              .map((sel) => {
                if (sel.value !== "all") {
                  return { value: sel.value, label: sel.label };
                } else if (sel.value === "all") {
                  all = true;
                  return null;
                }
                return sel;
              })
              .filter(Boolean)
          : [];

        newData[objectId] =
          all && newStaffs.length
            ? formatSelectOption(staffs, "name", "fr")
            : newStaffs;

        return newData;
      });
    },
    [staffs]
  );

  const onSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      if (!verifyTokenExpire()) return;

      setStartRequest(true);

      try {
        const endPoint = getEndpoint();
        let claimObjects = { ...data };
        let values = {};

        for (const claimObjectId in claimObjects) {
          let processings = claimObjects[claimObjectId];
          values[claimObjectId] = processings
            ? processings.map((processing) => processing.value)
            : null;
        }

        let submitEndpoint = "";
        if (
          verifyPermission(
            userPermissions,
            "update-processing-circuit-any-institution"
          )
        ) {
          submitEndpoint = `${endPoint.list}/${institutionId}`;
        } else {
          submitEndpoint = endPoint.list;
        }

        await axios.put(submitEndpoint, values);
        ToastBottomEnd.fire(toastAddSuccessMessageConfig());
      } catch (error) {
        console.error("Error submitting data:", error);
        ToastBottomEnd.fire(toastAddErrorMessageConfig());
      } finally {
        setStartRequest(false);
      }
    },
    [data, getEndpoint, institutionId, userPermissions]
  );

  const onChangeInstitution = useCallback(async (selected) => {
    if (!verifyTokenExpire()) return;

    setInstitutionId(selected.value);
    setInstitution(selected);

    try {
      const response = await axios.get(
        `${appConfig.apiDomaine}/any/staff-claim-object/${selected.value}`
      );

      let newObjectData = [];
      response.data.claimCategories.forEach((claimCategory) => {
        claimCategory.claim_objects.forEach(
          (claimObject) =>
            (newObjectData[claimObject.id] = claimObject.staffs.map((staff) => ({
              value: staff.id,
              label: response.data.staffs.map(
                    (staffTemp) => staffTemp.id === staff.id ? `${staffTemp.identite.firstname} ${staffTemp.identite.lastname}` : ''
                  ),
            })))
        );
      });

      setData(newObjectData);
      setClaimObject(response.data.claimCategories);
      setStaffs(response.data.staffs);
    } catch (error) {
      console.error("Error fetching institution data:", error);
    }
  }, []);

  return {
    loading,
    claimObject,
    staffs,
    data,
    institutionData,
    institution,
    institutionId,
    error,
    startRequest,
    onChangeProcessing,
    onSubmit,
    onChangeInstitution,
  };
};
